import React, { useEffect, useState } from "react";
import SEO from "../components/SEO";
import { Lightbulb, CheckCircle, Shield, Heart, Award, Gem, Scale, Users, ArrowRight } from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>> = {
  lightbulb: Lightbulb,
  verified: CheckCircle,
  security: Shield,
  favorite: Heart,
  deployed_code: Award,
  diamond: Gem,
  balance: Scale,
  groups: Users,
};

interface AboutSettings {
  about_hero_heading1?: string;
  about_hero_heading2?: string;
  about_hero_desc?: string;
  about_stat1_value?: string;
  about_stat1_label?: string;
  about_stat2_value?: string;
  about_stat2_label?: string;
  about_stat3_value?: string;
  about_stat3_label?: string;
  about_stat4_value?: string;
  about_stat4_label?: string;
  about_value1_title?: string;
  about_value1_desc?: string;
  about_value2_title?: string;
  about_value2_desc?: string;
  about_value3_title?: string;
  about_value3_desc?: string;
  about_value4_title?: string;
  about_value4_desc?: string;
}

const DEFAULTS: Required<AboutSettings> = {
  about_hero_heading1: "Precision-Engineered",
  about_hero_heading2: "Digital Solutions.",
  about_hero_desc: "We build digital tools, website software, and security systems that just work. Our team takes care of the technical heavy lifting so you can focus on growing your business and learning new skills.",
  about_stat1_value: "15+",
  about_stat1_label: "Years Experience",
  about_stat2_value: "500+",
  about_stat2_label: "Projects Completed",
  about_stat3_value: "95%",
  about_stat3_label: "Job Placement",
  about_stat4_value: "NG",
  about_stat4_label: "Nationwide Reach",
  about_value1_title: "Innovation",
  about_value1_desc: "We don't just follow trends; we engineer new trajectories. Every problem is a chance to reinvent the wheel—better.",
  about_value2_title: "Excellence",
  about_value2_desc: "Good enough is never enough. We strive for pixel-perfect precision and logic-perfect backend execution.",
  about_value3_title: "Integrity",
  about_value3_desc: "Trust is our most valuable currency. We operate with radical transparency and ethical fortitude.",
  about_value4_title: "Customer Focus",
  about_value4_desc: "Technology serves humans, not the other way around. We design with empathy and deploy with care.",
};

const VALUE_ICONS = ["lightbulb", "verified", "security", "favorite"];
const VALUE_BG_ICONS = ["deployed_code", "diamond", "balance", "groups"];

const AboutScreen: React.FC = () => {
  const [content, setContent] = useState<Required<AboutSettings>>(DEFAULTS);

  useEffect(() => {
    fetch("/api/settings")
      .then((r) => r.json())
      .then((data: Record<string, string>) => {
        setContent({
          about_hero_heading1: data.about_hero_heading1 || DEFAULTS.about_hero_heading1,
          about_hero_heading2: data.about_hero_heading2 || DEFAULTS.about_hero_heading2,
          about_hero_desc: data.about_hero_desc || DEFAULTS.about_hero_desc,
          about_stat1_value: data.about_stat1_value || DEFAULTS.about_stat1_value,
          about_stat1_label: data.about_stat1_label || DEFAULTS.about_stat1_label,
          about_stat2_value: data.about_stat2_value || DEFAULTS.about_stat2_value,
          about_stat2_label: data.about_stat2_label || DEFAULTS.about_stat2_label,
          about_stat3_value: data.about_stat3_value || DEFAULTS.about_stat3_value,
          about_stat3_label: data.about_stat3_label || DEFAULTS.about_stat3_label,
          about_stat4_value: data.about_stat4_value || DEFAULTS.about_stat4_value,
          about_stat4_label: data.about_stat4_label || DEFAULTS.about_stat4_label,
          about_value1_title: data.about_value1_title || DEFAULTS.about_value1_title,
          about_value1_desc: data.about_value1_desc || DEFAULTS.about_value1_desc,
          about_value2_title: data.about_value2_title || DEFAULTS.about_value2_title,
          about_value2_desc: data.about_value2_desc || DEFAULTS.about_value2_desc,
          about_value3_title: data.about_value3_title || DEFAULTS.about_value3_title,
          about_value3_desc: data.about_value3_desc || DEFAULTS.about_value3_desc,
          about_value4_title: data.about_value4_title || DEFAULTS.about_value4_title,
          about_value4_desc: data.about_value4_desc || DEFAULTS.about_value4_desc,
        });
      })
      .catch(() => {/* use defaults on error */ });
  }, []);

  const stats = [
    { value: content.about_stat1_value, label: content.about_stat1_label },
    { value: content.about_stat2_value, label: content.about_stat2_label },
    { value: content.about_stat3_value, label: content.about_stat3_label },
    { value: content.about_stat4_value, label: content.about_stat4_label },
  ];
  const missionValues = [
    { title: content.about_value1_title, desc: content.about_value1_desc, icon: VALUE_ICONS[0], bgIcon: VALUE_BG_ICONS[0] },
    { title: content.about_value2_title, desc: content.about_value2_desc, icon: VALUE_ICONS[1], bgIcon: VALUE_BG_ICONS[1] },
    { title: content.about_value3_title, desc: content.about_value3_desc, icon: VALUE_ICONS[2], bgIcon: VALUE_BG_ICONS[2] },
    { title: content.about_value4_title, desc: content.about_value4_desc, icon: VALUE_ICONS[3], bgIcon: VALUE_BG_ICONS[3] },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness", "EducationalOrganization"],
    name: "InnovaTech Consultancy Limited",
    alternateName: "InnovaTech",
    url: "https://www.innovatech-ng.com",
    logo: "https://www.innovatech-ng.com/logo/lightmode-logo.webp",
    image: "https://www.innovatech-ng.com/logo/lightmode-logo.webp",
    founder: {
      "@type": "Person",
      name: "Abdullahi Bala Musa",
      jobTitle: "Founder & Lead Technical Consultant",
      knowsAbout: ["Cybersecurity", "Network Security", "Digital Transformation", "Software Engineering"],
    },
    foundingDate: "2020",
    address: {
      "@type": "PostalAddress",
      streetAddress: "No.2 Salmaniyya Plaza, Opp. TCN, Ibrahim Aliyu Bypass",
      addressLocality: "Dutse",
      addressRegion: "Jigawa",
      postalCode: "720101",
      addressCountry: "NG",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 11.6928,
      longitude: 9.348,
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+234 813 023 7608",
      contactType: "customer service",
      areaServed: "NG",
    },
    sameAs: [
      "https://x.com/innovatech_ng",
      "https://instagram.com/innovatech-ng",
      "https://linkedin.com/company/innovatech-ng",
    ],
    description:
      "Nigeria's premier ICT solutions provider, tech academy, and digital transformation partner in Jigawa State and nationwide.",
    knowsAbout: [
      "Custom Web Development",
      "Cybersecurity Defense",
      "SIWES Industrial Training",
      "CCTV & Physical Surveillance",
      "IT Consultancy",
      "Tech Academy Bootcamps",
    ],
  };

  return (
    <div className="animate-fade-in">
      <SEO
        title="About Us | InnovaTech"
        description="Learn about InnovaTech Consultancy Limited: Leading ICT solutions provider, software engineering tech partner, and digital institute in Dutse, Jigawa State & Nigeria."
        keywords="about InnovaTech, IT company Dutse Jigawa, Abdullahi Bala Musa, tech training center Jigawa, ICT consultancy Northern Nigeria"
        canonical="/about"
      >
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </SEO>

      <header className="relative flex min-h-[60vh] flex-col items-center justify-center pt-24 pb-12">
        <div
          className="absolute inset-0 bg-dot-pattern dark:bg-none opacity-60 z-0 pointer-events-none"
          style={{
            maskImage:
              "linear-gradient(to bottom, black 40%, transparent 100%)",
          }}
        ></div>
        <div className="relative z-10 container mx-auto px-4 text-center max-w-4xl">
          <div className="mb-6 inline-flex items-center justify-center rounded-full border border-border-subtle dark:border-white/10 bg-surface dark:bg-white/5 px-3 py-1 shadow-sm">
            <span className="mr-2 h-2 w-2 rounded-full bg-primary animate-pulse"></span>
            <span className="text-xs font-medium text-text-sub dark:text-gray-300 uppercase tracking-wider">
              Engineering The Future
            </span>
          </div>
          <h1 className="mb-6 font-display text-5xl md:text-7xl font-bold tracking-tighter text-text-main dark:text-white leading-[1.1]">
            {content.about_hero_heading1}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-text-main to-text-sub dark:from-white dark:to-gray-400">
              {content.about_hero_heading2}
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg md:text-xl text-text-sub dark:text-gray-400 leading-relaxed">
            {content.about_hero_desc}
          </p>

          {/* InnovaTech Identity Stats Block */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="p-4 bg-surface dark:bg-white/5 rounded-xl border border-border-subtle dark:border-white/10 shadow-sm">
                <div className="text-3xl font-bold text-primary dark:text-red-400 mb-1">{stat.value}</div>
                <div className="text-xs font-medium text-text-sub dark:text-gray-400 uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <div className="h-16 w-[1px] bg-gradient-to-b from-border-subtle dark:from-white/10 to-primary"></div>
          </div>
        </div>
      </header>

      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-center font-display text-3xl font-bold tracking-tight mb-20 text-text-main dark:text-white">
            Our Evolution
          </h2>
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0">
            {/* Central Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-[2px] bg-border-subtle dark:bg-white/10 -translate-x-1/2"></div>
            <div className="hidden md:block absolute left-1/2 top-0 h-[60%] w-[2px] bg-primary -translate-x-1/2 z-0"></div>

            {/* Item 1 */}
            <div className="md:pr-12 md:text-right flex flex-col items-center md:items-end relative group">
              <div className="hidden md:block absolute right-[-6px] top-6 h-3 w-3 rounded-full border-2 border-primary bg-surface dark:bg-surface-dark z-10 translate-x-[50%] shadow-[0_0_0_4px_rgba(238,46,43,0.1)]"></div>
              <div className="w-full bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30">
                <span className="text-primary dark:text-red-400 font-mono text-sm font-bold tracking-wider mb-2 block">
                  2020
                </span>
                <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                  The Beginning
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed">
                  InnovaTech started in 2020 with three friends who shared a
                  common frustration: watching businesses struggle with
                  technology while international competitors raced ahead. We
                  were fresh graduates with big dreams and determination to make
                  a difference. Our first office was a small room in Dutse,
                  Jigawa State - barely enough space for our three desks.
                </p>
              </div>
            </div>
            <div className="hidden md:block"></div>

            {/* Item 2 */}
            <div className="hidden md:block"></div>
            <div className="md:pl-12 flex flex-col items-center md:items-start relative group mt-8 md:mt-24">
              <div className="hidden md:block absolute left-[-6px] top-6 h-3 w-3 rounded-full border-2 border-primary bg-primary z-10 translate-x-[-50%] shadow-[0_0_0_8px_rgba(238,46,43,0.15)]"></div>
              <div className="w-full bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30">
                <span className="text-primary dark:text-red-400 font-mono text-sm font-bold tracking-wider mb-2 block">
                  2021
                </span>
                <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                  The Turning Point
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed">
                  A mid-sized retail company gave us a chance to build their
                  entire e-commerce platform. Skeptical at first, they saw us
                  pour everything into the project. We delivered ahead of
                  schedule, and their sales increased by 300% in the first
                  quarter. That success story opened doors we never imagined.
                </p>
              </div>
            </div>

            {/* Item 3 */}
            <div className="md:pr-12 md:text-right flex flex-col items-center md:items-end relative group mt-8 md:mt-24">
              <div className="hidden md:block absolute right-[-6px] top-6 h-3 w-3 rounded-full border-2 border-primary bg-primary z-10 translate-x-[50%] shadow-[0_0_0_8px_rgba(238,46,43,0.15)]"></div>
              <div className="w-full bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30">
                <span className="text-primary dark:text-red-400 font-mono text-sm font-bold tracking-wider mb-2 block">
                  2022
                </span>
                <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                  Rapid Growth
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed">
                  By 2022, we had grown to a team of 15 and moved into a proper
                  office. We specialized in web development, mobile apps, and
                  network infrastructure. Noticing a talent gap in the market,
                  we laid the groundwork for what would become our educational
                  arm.
                </p>
              </div>
            </div>
            <div className="hidden md:block"></div>

            {/* Item 4 */}
            <div className="hidden md:block"></div>
            <div className="md:pl-12 flex flex-col items-center md:items-start relative group mt-8 md:mt-24">
              <div className="hidden md:block absolute left-[-6px] top-6 h-3 w-3 rounded-full border-2 border-border-subtle dark:border-white/10 bg-surface dark:bg-surface-dark z-10 translate-x-[-50%]"></div>
              <div className="w-full bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30">
                <span className="text-text-sub dark:text-gray-500 font-mono text-sm font-bold tracking-wider mb-2 block">
                  2023
                </span>
                <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                  InnovaTech Digital Institute
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed">
                  We launched the Institute with 20 students in our first batch.
                  Today, we've trained over 1,000 students, with 95% employed in
                  tech roles. Watching our students succeed gives us as much
                  satisfaction as delivering a perfect project.
                </p>
              </div>
            </div>

            {/* Item 5 */}
            <div className="md:pr-12 md:text-right flex flex-col items-center md:items-end relative group mt-8 md:mt-24">
              <div className="hidden md:block absolute right-[-6px] top-6 h-3 w-3 rounded-full border-2 border-primary bg-primary z-10 translate-x-[50%] shadow-[0_0_0_8px_rgba(238,46,43,0.15)]"></div>
              <div className="w-full bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30">
                <span className="text-primary dark:text-red-400 font-mono text-sm font-bold tracking-wider mb-2 block">
                  2024
                </span>
                <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                  Resilience & Innovation
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed">
                  Navigating economic shifts and the post-pandemic landscape
                  challenged us, but also pushed us to perfect remote
                  collaboration tools. Each challenge made us stronger, more
                  innovative, and more efficient than ever.
                </p>
              </div>
            </div>
            <div className="hidden md:block"></div>

            {/* Item 6 */}
            <div className="hidden md:block"></div>
            <div className="md:pl-12 flex flex-col items-center md:items-start relative group mt-8 md:mt-24">
              <div className="hidden md:block absolute left-[-6px] top-6 h-3 w-3 rounded-full border-2 border-border-subtle dark:border-white/10 bg-surface dark:bg-surface-dark z-10 translate-x-[-50%]"></div>
              <div className="w-full bg-surface dark:bg-surface-dark border border-border-subtle dark:border-white/10 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30">
                <span className="text-text-sub dark:text-gray-500 font-mono text-sm font-bold tracking-wider mb-2 block">
                  2025
                </span>
                <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                  Global Impact
                </h3>
                <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed">
                  Today, InnovaTech is a team of 50+ professionals serving
                  clients across Nigeria and beyond. Completing over 500
                  projects, we are proudest of the relationships we've built. We
                  are investing heavily in AI, blockchain, and IoT to ensure the
                  next generation of African tech talent is future-ready.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface dark:bg-surface-dark border-t border-border-subtle dark:border-white/10">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-text-main dark:text-white">
                Our Mission
              </h2>
              <p className="mt-4 text-text-sub dark:text-gray-400 max-w-lg">
                Our foundational values are the load-bearing walls of our
                organization. They dictate how we build, how we interact, and
                how we grow.
              </p>
            </div>
            <button className="group flex items-center gap-2 text-sm font-bold text-primary dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors">
              View our culture deck
              <ArrowRight className="w-[18px] h-[18px] transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {missionValues.map((val) => (
              <div key={val.title} className="group relative overflow-hidden rounded-xl border border-border-subtle dark:border-white/10 bg-canvas dark:bg-canvas-dark p-8 hover:border-primary/40 transition-colors duration-300 h-80 flex flex-col justify-between">
                <div className="absolute top-0 right-0 p-8 opacity-20 dark:opacity-10 group-hover:opacity-40 transition-opacity">
                  {(() => {
                    const IconComponent = ICON_MAP[val.bgIcon];
                    return IconComponent ? (
                      <IconComponent
                        size={120}
                        strokeWidth={0.5}
                        className="text-text-sub dark:text-white"
                      />
                    ) : null;
                  })()}
                </div>
                <div className="z-10">
                  <div className="h-10 w-10 rounded-lg bg-white dark:bg-white/5 border border-border-subtle dark:border-white/10 flex items-center justify-center mb-6 shadow-sm text-primary dark:text-red-400">
                    {(() => {
                      const IconComponent = ICON_MAP[val.icon];
                      return IconComponent ? <IconComponent className="w-5 h-5" /> : null;
                    })()}
                  </div>
                  <h3 className="text-xl font-bold text-text-main dark:text-white mb-2">
                    {val.title}
                  </h3>
                  <p className="text-text-sub dark:text-gray-400 text-sm leading-relaxed max-w-xs">
                    {val.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutScreen;
